import React, { FunctionComponent, h } from "preact"; // eslint-disable-line @typescript-eslint/no-unused-vars
import { useEffect, useState } from "preact/hooks";

import cx from "classnames";

import utils from "../../helpers/utils";

interface IDividendCountry {
  countryName: string;
  dividendAmount: number;
  currency: string;
  currencySymbol: string;
  currencyDivRateDecimals: number;
  currencyTotalValueDecimals: number;
}

interface IAppProps {
  countries: IDividendCountry[];
}

const App: FunctionComponent<IAppProps> = ({ countries }) => {
  const [numberOfShares, setNumberOfShares] = useState(0);
  const [selectedCountry, setSelectedCountry] = useState(countries[0].countryName);
  const [result, setResult] = useState("");
  const [countryError, setCountryError] = useState(false);
  const [sharesError, setSharesError] = useState(false);

  useEffect(() => {
    if (sharesError) {
      setSharesError(!numberOfShares || !utils.isANumber(numberOfShares));
    }
  }, [numberOfShares]);

  const onSharesInput = (shareNumber: string) => {
    setNumberOfShares(parseInt(shareNumber, 10));
  };

  const selectCountry = (e) => setSelectedCountry(e.target.value);

  const calculate = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const selectedItem = countries.find((i) => i.countryName === selectedCountry);

    if (!selectedCountry) {
      setCountryError(true);
      return;
    }

    if (!utils.isANumber(numberOfShares)) {
      setSharesError(true);
      return;
    }

    if (selectedItem) {
      const { currencySymbol, dividendAmount, currencyTotalValueDecimals, currencyDivRateDecimals } = selectedItem;
      const res: any = ((dividendAmount.toFixed(currencyDivRateDecimals ?? 2) as any) * numberOfShares).toFixed(
        currencyTotalValueDecimals ?? 2
      );
      setResult(currencySymbol + (res / 100).toFixed(currencyTotalValueDecimals ?? 2));
    }
  };
  return (
    <div>
      <form
        onSubmit={calculate}
        className="dividend-calculator__form"
      >
        <h3>Calculate your dividend payment</h3>

        <fieldset>
          <label htmlFor="shares-no">Enter the amount of shares that you own</label>
          <input
            type="number"
            id="shares-no"
            className={cx({ error: sharesError })}
            value={numberOfShares}
            placeholder="Amount as a whole number"
            step={1}
            min={0}
            onInput={(e) => onSharesInput((e.target as HTMLInputElement).value)} // $event is of any type, need to cast https://stackoverflow.com/a/44321394/3263594
          />
          {sharesError && <p className="error-text">Please enter a numeric value</p>}
        </fieldset>
        <fieldset>
          <label htmlFor="shares-held-in">Which country are your shares held in?</label>
          <select
            id="shares-held-in"
            value={selectedCountry}
            onChange={selectCountry}
            className={cx({ error: countryError })}
          >
            {countries.map((country, key) => (
              <option
                key={key}
                value={country.countryName}
              >
                {country.countryName}
              </option>
            ))}
          </select>
          {countryError && <p className="error-text">Please select a country from the list</p>}
        </fieldset>

        <button
          type="submit"
          className="button button--primary button--arrow"
          aria-label="label"
          onClick={calculate}
        >
          Calculate
        </button>
      </form>

      {result && (
        <div className="alert-block alert-block--no-icon">
          <div className="alert-block__inner">
            <h4 className="alert-block__title">Total value of your dividend</h4>
            <p className="alert-block__description">{result}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default App;
